import { invoiceIngredientKey } from '@alexiarest/shared';
import { ingredientCostShares } from '@alexiarest/shared';
import type { InboxEntry } from '@alexiarest/shared';
import { normalizeIngredientName, type RecipeProposal, type Unit, type PriceEstimate } from '@alexiarest/shared';
import { purchaseUnitSchema, catalogCompleteness, nextQuestions, skipQuestion, puedeEscanear, inboundEmailSinEscanerMessage, type QuestionHistory } from '@alexiarest/shared';
import { DRAFT_TTL_SECONDS, draftIsExpired, type Draft } from '@alexiarest/shared';
import {
  allocateTaxPayment,
  amortizationSchedule,
  analyzePricing,
  estimatePrice,
  netPriceOf,
  benchmarkKpis,
  businessOverview,
  buildContext,
  buildExpenseDraft,
  fusionarPaginas,
  ivaEnFrase,
  type ScannedDocument,
  buscarDuplicado,
  invoiceLinesToCatalogProposal,
  catalogPriceInput,
  buildInventoryDraft,
  cashAlerts,
  comparePlatforms,
  compareTargets,
  computeCashFlow,
  computeKpis,
  computeProfitAndLoss,
  computeRecipeCost,
  computeRecipeCostPartial,
  CostingError,
  UnitError,
  currentMonth,
  demoDataset,
  findCostDeviations,
  ingredientDisplayName,
  inPeriod,
  menuEngineering,
  unitsSoldFor,
  withUnitsForLocation,
  monthlyProfitAndLoss,
  payables,
  PREDEFINED_ROLES,
  PLANS,
  scanQuota,
  separarRepetidos,
  ultimaFechaDePago,
  projectCashFlow,
  purchaseFormatLabel,
  planRecipeProposal,
  RECIPE_FIXTURES,
  proposalToImportRows,
  totalDue,
  valueInventoryLines,
  weekdayPerformance,
} from '@alexiarest/shared';
import type {
  PurchaseDocumentType,
  AdminOverview,
  CashMovement,
  CashMovementInput,
  CustomRole,
  CustomRoleInput,
  DailyRevenue,
  DailyRevenueInput,
  ExpenseEntry,
  ExpenseInput,
  FinanceInput,
  Ingredient,
  IngredientInput,
  Investment,
  InvestmentInput,
  InventoryCount,
  InventoryCountInput,
  Membership,
  MenuItem,
  MenuItemInput,
  Period,
  PricingAnalysis,
  Recipe,
  CostingCulprit,
  RecipeCost,
  RecipeInput,
  Supplier,
  Target,
  TargetInput,
  Tenant,
  TenantSettings,
} from '@alexiarest/shared';
import type {
  AlexiaRestApi,
  BatchResult,
  ConfirmRecipeProposalInput,
  ConfirmRecipeProposalResponse,
  IngredientBatch,
  InventoryScanResult,
  RecipeBatch,
  RecipeImportInput,
  RecipeProposalResponse,
  SupplierInput,
} from './contract';
import {
  guessRevenueColumns,
  guessUnitsColumns,
  helpScreenById,
  normalizeText,
  ingredientUsage,
  ingredientUsageMessage,
  recipeUsage,
  recipeUsageMessage,
  targetKey,
  type PosIntegrationsResponse,
  type BillingInterval,
} from '@alexiarest/shared';
import { demoChat, demoSuggestions, type DemoAssistantData } from './demoAssistant';
import { ApiError } from './error';
import type {
  AmortizationRow,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantSuggestion,
  CashFlowResponse,
  CostDeviation,
  DashboardResponse,
  KpisResponse,
  MeResponse,
  TeamResponse,
  PayablesResponse,
  MapeoVentasPropuesta,
  PricingResponse,
  RecipeCostsResponse,
  ReconciliationResult,
  TaxAllocationRequest,
  TenantUpdate,
} from './types';

/* ------------------------------------------------------------------ */
/* Estado en memoria                                                   */
/* ------------------------------------------------------------------ */

const TENANT_ID = 'demo';

interface DemoState {
  tenant: Tenant;
  user: Membership;
  settings: TenantSettings;
  suppliers: Supplier[];
  ingredients: Ingredient[];
  recipes: Recipe[];
  menuItems: MenuItem[];
  revenues: DailyRevenue[];
  expenses: ExpenseEntry[];
  investments: Investment[];
  cashMovements: CashMovement[];
  targets: Target[];
  inventoryCounts: InventoryCount[];
  /** Roles personalizados (mejora 70): en la demo se pueden montar y probar. */
  customRoles: CustomRole[];
}

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;

/** Fecha de dentro de N dias, en YYYY-MM-DD. */
function enDias(dias: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + dias);
  return d.toISOString().slice(0, 10);
}

function initialState(): DemoState {
  const settings = clone(demoDataset.settings);
  return {
    tenant: {
      tenantId: TENANT_ID,
      name: 'Trattoria Marina',
      /*
       * Mexico y pesos, no España y euros: la demo enseña la cuenta de Alex y
       * el resto de la ficha (RFC, direccion de CDMX) ya es mexicana. Ver un
       * negocio mexicano facturando en euros parece que la moneda no funciona
       * (Alex, 2 sep 2026).
       */
      country: 'MX',
      currency: 'MXN',
      plan: 'trial',
      subscriptionStatus: 'trialing',
      /*
       * La prueba se mueve con la demo, no se queda clavada a una fecha: el
       * 15 de agosto de 2026 ya habia pasado y la demo se ensenyaba con la
       * prueba caducada. Dos semanas por delante, siempre.
       */
      trialEndsAt: enDias(14),
      /*
       * Datos de factura de ejemplo: en la demo se enseña la pantalla llena,
       * que es la unica forma de que se entienda para que sirve.
       *
       * Son los de Alex a peticion suya (1 sep 2026): graba los tutoriales con
       * esta demo y quiere que la cuenta que sale sea la suya. El pais fiscal
       * es Mexico porque ABCA090909D45 tiene forma de RFC: con España, la
       * pantalla marcaria el identificador como invalido y saldria un error
       * rojo en mitad del video.
       */
      tax: {
        legalName: 'ALEJANDRO CORTIJO ROMEU',
        taxId: 'ABCA090909D45',
        taxCountry: 'MX',
        addressLine: 'Av. Insurgentes Sur 1234, piso 2',
        postalCode: '03100',
        city: 'Ciudad de México',
        province: 'CDMX',
      },
      settings,
      createdAt: demoDataset.period.start + 'T00:00:00.000Z',
    },
    user: {
      tenantId: TENANT_ID,
      userId: 'demo-user',
      email: 'demo@alexiarest.app',
      name: 'Cuenta de demostración',
      role: 'owner',
      createdAt: '2026-02-01T00:00:00.000Z',
    },
    settings,
    suppliers: [
      { tenantId: TENANT_ID, supplierId: 'sup-carnes', name: 'Carnes Roma' },
      { tenantId: TENANT_ID, supplierId: 'sup-pescados', name: 'Distmar' },
      { tenantId: TENANT_ID, supplierId: 'sup-general', name: 'Distribuciones del Norte' },
    ],
    ingredients: clone(demoDataset.ingredients),
    recipes: clone(demoDataset.recipes),
    menuItems: clone(demoDataset.menuItems),
    revenues: clone(demoDataset.revenues),
    expenses: clone(demoDataset.expenses),
    investments: clone(demoDataset.investments),
    cashMovements: clone(demoDataset.cashMovements) as CashMovement[],
    targets: clone(demoDataset.targets),
    inventoryCounts: clone(demoDataset.inventoryCounts),
    customRoles: [],
  };
}

let state: DemoState = initialState();
let purchasePresentations: string[] = [];
let drafts: Draft[] = [];
let inbox: InboxEntry[] = [];
let questionHistory: Record<string, QuestionHistory> = {};
let answeredDemoQuestions = new Set<string>();
const draftLinks = new Map<string, { id: string; expiresAt: number }>();
const confirmingDrafts = new Map<string, Promise<Draft>>();

/*
 * `?configurar` en la URL de la demo abre el cuestionario inicial como si
 * el restaurante acabara de darse de alta. Es para que Alex lo grabe y lo
 * ensenye sin tener que crear una cuenta: la demo normal entra directa.
 */
if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('configurar')) {
  const firstRecipe = new URLSearchParams(window.location.search).get('configurar') === 'receta';
  state.tenant.setup = { step: firstRecipe ? 5 : 2, recipeFirstPending: true };
  state.ingredients = [];
  state.recipes = [];
  state.menuItems = [];
}

/** Devuelve el estado a los datos de fábrica (usado desde Ajustes). */
export function resetDemoState(): void {
  purchasePresentations = [];
  state = initialState();
  demoLocation = { tenantId: TENANT_ID, locationId: 'principal', name: 'Principal', active: true };
  drafts = []; inbox = [];
  questionHistory = {}; answeredDemoQuestions = new Set();
}

/** Periodo por defecto que cubre todos los datos de demostración. */
export const demoPeriod: Period = { ...demoDataset.period };

let counter = 0;
function newId(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter}`;
}

const nowIso = (): string => new Date().toISOString();

/** Un registro sin centro pertenece al principal, como en la API real. */
const sameLocation = (a?: string, b?: string): boolean =>
  (a?.trim() || 'principal') === (b?.trim() || 'principal');

const notFound = (what: string) =>
  new ApiError('NOT_FOUND', `No hemos encontrado ${what}.`, 404);

/* ------------------------------------------------------------------ */
/* Calculo                                                             */
/* ------------------------------------------------------------------ */

function context() {
  return buildContext(state.ingredients, state.recipes);
}

let demoLocation: import("@alexiarest/shared").Location = { tenantId: TENANT_ID, locationId: "principal", name: "Principal", active: true };

function financeInput(): FinanceInput {
  return {
    locations: [demoLocation],
    revenues: state.revenues,
    // Igual que `analysis.ts`: una nota de venta nunca es un gasto contable.
    expenses: state.expenses.filter((e) => (e.documentType ?? 'factura') === 'factura'),
    investments: state.investments,
    cashMovements: state.cashMovements,
    inventoryCounts: state.inventoryCounts,
  };
}

function costOf(recipeId: string): RecipeCost {
  try {
    return computeRecipeCost(recipeId, context());
  } catch (error) {
    const message =
      error instanceof Error
        ? error.message
        : 'No hemos podido calcular el coste de la receta.';
    throw new ApiError('VALIDATION_ERROR', message, 400);
  }
}

/** Analisis de precios de toda la carta, saltando platos que no cuadran. */
interface PlatoSinCostear {
  menuItemId: string;
  name: string;
  reason: string;
  culprit?: CostingCulprit;
}

/**
 * Platos costeados y platos que no han podido costearse, CON su motivo.
 *
 * El motivo y el culpable se guardan al vuelo: la demo los tiraba y luego
 * reconstruia la lista por diferencia, asi que todos los platos salian con un
 * "No se ha podido calcular el coste" generico y sin manera de saber que
 * subreceta lo provocaba. La API si los tenia; aqui no.
 */
function pricingItems(): {
  rows: Array<{ item: MenuItem; analysis: PricingAnalysis; cost: RecipeCost }>;
  skipped: PlatoSinCostear[];
} {
  const rows: Array<{ item: MenuItem; analysis: PricingAnalysis; cost: RecipeCost }> = [];
  const skipped: PlatoSinCostear[] = [];
  const ctx = context();
  // Igual que la API real: los inactivos tambien se costean (el filtro de
  // estado de la pantalla decide que se ve). Saltarselos aqui escondia en la
  // demo lo que en produccion si aparecia (mejora 60 de Alex, 6 sep 2026).
  for (const item of state.menuItems) {
    try {
      const cost = computeRecipeCostPartial(item.recipeId, ctx);
      rows.push({ item, analysis: analyzePricing(item, cost, state.settings), cost });
    } catch (err) {
      // Un plato mal configurado no puede tumbar toda la pantalla de precios.
      skipped.push({
        menuItemId: item.menuItemId,
        name: item.name,
        reason:
          err instanceof Error ? err.message : 'No se ha podido calcular el coste de la receta.',
        culprit:
          err instanceof CostingError || err instanceof UnitError ? err.culprit : undefined,
      });
    }
  }
  return { rows, skipped };
}

function payablesResponse(today: string): PayablesResponse {
  return { items: payables(state.expenses, today), totalDue: totalDue(state.expenses, today) };
}

/** Fecha de hoy en formato YYYY-MM-DD, sin depender del huso. */
/* --- Escaner de facturas (demo) --- */

/** Contador de escaneos de la sesion de demo. Se reinicia con el estado. */
let demoScans = 0;

const currentMonthDemo = () => todayIso().slice(0, 7);

/**
 * Factura de ejemplo del escaner. Son los campos tal y como los devolveria
 * Textract, para que la demo recorra EXACTAMENTE el mismo motor de reglas que
 * la app real: si el desglose de base e IVA se rompiera, se romperia aqui.
 *
 * El IVA es del 16 % porque la demo es mexicana: desde que el motor distingue
 * un solo tipo de varios (28 sep 2026), un 10 % espanol en Mexico no es un
 * tipo real y el cajon lo ensenaria como «Varios».
 */
const FACTURA_DEMO = {
  vendorName: { value: 'DISTRIBUCIONES DEL NORTE, S.L.', confidence: 98 },
  /*
   * Fecha y numero de HOY, no clavados.
   *
   * Estaban fijos en agosto de 2026 y envejecieron: en cuanto paso el mes, la
   * factura de ejemplo caia en un mes sin datos y el gasto que creaba no se
   * veia por ningun lado, ni en el periodo de la demo ni en el mes en curso.
   * La demo entera se mueve en el tiempo; esto tambien.
   */
  invoiceNumber: { value: `F-${todayIso().slice(0, 4)}/${todayIso().slice(5).replace('-', '')}`, confidence: 94 },
  invoiceDate: { value: todayIso().split('-').reverse().join('/'), confidence: 96 },
  subtotal: { value: '486,20', confidence: 95 },
  tax: { value: '77,79', confidence: 95 },
  total: { value: '563,99', confidence: 99 },
  rawText: 'DISTRIBUCIONES DEL NORTE SL Alimentación Ternera Merluza Verduras',
  lines: [
    { description: 'Solomillo de ternera', quantity: '10 kg', unitPrice: '20,00', total: '200,00' },
    { description: 'Merluza', quantity: '8 kg', unitPrice: '24,00', total: '192,00' },
    { description: 'Verduras variadas', quantity: '10 kg', unitPrice: '9,42', total: '94,20' },
  ],
};

/**
 * Factura de ejemplo de DOS paginas y tres tipos de IVA, calcada (con otro
 * nombre) de la que destapo el fallo de los PDF largos en soporte el 21 sep
 * 2026. Son las dos lecturas por pagina, como las deja la API tras partir el
 * PDF, y pasan por `fusionarPaginas` y el mismo motor: el pie con el total
 * repetido en las dos hojas, el subtotal acumulado de la primera (122,35), el
 * «Descuento 8,78» que no es un porcentaje y el abono de envase en negativo.
 */
const facturaDemoDosPaginas = (): ScannedDocument[] => {
  const fecha = todayIso().split('-').reverse().join('/');
  const pie = ['Descuento', '21,00%', '4,03', '0,85', '8,78', '%', '%', '10,00%', '75,92', '7,59', '4,00%', '39,69', '1,59', 'Total Factura', '129,67 €'];
  const cabecera = {
    vendorName: { value: 'Bodegas del Levante', confidence: 96 },
    invoiceNumber: { value: `A/${todayIso().slice(2, 4)}${todayIso().slice(5).replace('-', '')}`, confidence: 99 },
    invoiceDate: { value: fecha, confidence: 99 },
    dueDate: { value: `${fecha} 129,67`, confidence: 97 },
    paymentMethod: { value: 'DOMICILIACION', confidence: 95 },
    total: { value: '129,67 €', confidence: 90 },
  };
  return [
    {
      ...cabecera,
      subtotal: { value: '122,35', confidence: 97 },
      lines: [
        { description: 'LAVAVAJILLAS MANUAL 5L', quantity: '1,000', unitPrice: '7,8500', total: '7,54' },
        { description: 'PALETA YORK LONCHAS 500 GR', quantity: '4,000', unitPrice: '4,2500', total: '16,15' },
        { description: 'PAN BARRITA DE AGUA 140G C/46U', quantity: '1,000', unitPrice: '22,5500', total: '19,84' },
        { description: 'SALMON NORUEGO 500G', quantity: '1,000', unitPrice: '26,9000', total: '25,55' },
        { description: 'TOMATE RALLADO NATURAL 1,1L C/6U', quantity: '2,000', unitPrice: '7,1900', total: '13,52' },
        { description: 'PAN BARRITA DE AGUA 140G C/46U', quantity: '1,000', unitPrice: '22,5500', total: '19,84' },
        { description: 'MERMELADA FRESA PORCIONES', quantity: '1,000', unitPrice: '9,1500', total: '9,15' },
        { description: 'MERMELADA MELOCOTON PORCIONES', quantity: '1,000', unitPrice: '9,1500', total: '9,15' },
      ],
      rawText: 'Bodegas del Levante LAVAVAJILLAS MANUAL 5L PALETA YORK PAN BARRITA SALMON TOMATE MERMELADA',
      fullText: ['Subtotal', '122,35', ...pie].join('\n'),
    },
    {
      ...cabecera,
      subtotal: { value: '119,64', confidence: 98 },
      lines: [{ description: 'ENVASE CERVEZA 24/3', quantity: '-1,000', unitPrice: '3,5100', total: '-3,51' }],
      rawText: 'Bodegas del Levante ENVASE CERVEZA 24/3',
      fullText: ['Subtotal', '119,64', ...pie].join('\n'),
    },
  ];
};

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Suma dias a una fecha YYYY-MM-DD. */
function addDaysIso(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** Lo que necesita Alexia para responder: los mismos datos, en crudo. */
function assistantData(): DemoAssistantData {
  return {
    settings: state.settings,
    ingredients: state.ingredients,
    recipes: state.recipes,
    menuItems: state.menuItems,
    revenues: state.revenues,
    expenses: state.expenses,
    investments: state.investments,
    cashMovements: state.cashMovements,
    targets: state.targets,
  };
}

/** Pausa corta para que la pantalla enseñe su estado de "pensando". */
const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Respuesta del manual para preguntas de USO («como hago», «que es»,
 * «donde»). Las de datos («cuanto vendi», «que dia vendo mas») siguen al
 * asistente de datos aunque compartan palabras con el manual. Se elige la
 * FAQ o el parrafo con mas palabras en comun; con menos de dos, nada.
 */
const INTENCION_USO = /^[¿\s]*(c[oó]mo|d[oó]nde|puedo|qu[eé] es|qu[eé] significa|para qu[eé]|por qu[eé])\b/i;
function faqParecida(screenId: string, pregunta: string): string | null {
  if (!INTENCION_USO.test(pregunta.trim())) return null;
  const screen = helpScreenById(screenId);
  if (!screen) return null;
  const VACIAS = new Set(['como', 'que', 'para', 'con', 'los', 'las', 'una', 'del', 'por', 'sin', 'mis', 'tengo', 'puedo', 'hago', 'donde', 'significa']);
  const palabras = (texto: string) =>
    new Set(normalizeText(texto).split(' ').filter((w) => w.length > 2 && !VACIAS.has(w)));
  const mias = palabras(pregunta);
  const candidatos = [
    ...screen.faqs.map((faq) => ({ clave: faq.q, respuesta: faq.a })),
    ...screen.howItWorks.map((p) => ({ clave: p, respuesta: p })),
  ];
  let mejor: { puntos: number; respuesta: string } | null = null;
  for (const c of candidatos) {
    const suyas = palabras(c.clave);
    let puntos = 0;
    for (const w of mias) if (suyas.has(w)) puntos += 1;
    if (puntos >= 2 && (!mejor || puntos > mejor.puntos)) mejor = { puntos, respuesta: c.respuesta };
  }
  return mejor?.respuesta ?? null;
}

/**
 * Doble del estimador del modelo para la demo: en España sale de la tabla
 * local, como en producción; en el resto de países el modelo estima en la
 * moneda del tenant, y aquí lo imitan unas cifras fijas (pesos para México,
 * magnitudes de euro/dólar para cualquier otro). Nunca entran en la base real.
 */
function demoPriceEstimate(name: string, unit: Unit, country: string, currency: string): PriceEstimate | null {
  const local = estimatePrice(name, unit, country, currency);
  if (local) return local;
  const mexico = country === 'MX' && currency === 'MXN';
  const fixtures: Record<string, { unitPrice: number; unit: Unit }> = mexico ? {
    'arroz carnaroli': { unitPrice: 95, unit: 'kg' }, 'caldo verdura': { unitPrice: 45, unit: 'l' },
    'boletus': { unitPrice: 480, unit: 'kg' }, 'harina': { unitPrice: 24, unit: 'kg' },
    'azucar': { unitPrice: 28, unit: 'kg' }, 'mantequilla': { unitPrice: 180, unit: 'kg' },
  } : {
    'arroz carnaroli': { unitPrice: 3.4, unit: 'kg' }, 'caldo verdura': { unitPrice: 1.6, unit: 'l' },
    'boletus': { unitPrice: 18, unit: 'kg' }, 'harina': { unitPrice: 0.9, unit: 'kg' },
    'azucar': { unitPrice: 1.1, unit: 'kg' }, 'mantequilla': { unitPrice: 7.5, unit: 'kg' },
  };
  const value = fixtures[normalizeIngredientName(name)];
  return value && value.unit === unit ? { ...value, basis: 'alexia', at: '2026-09-14' } : null;
}

export function previewDemoRecipe(proposal: RecipeProposal) {
  const rows = proposalToImportRows(proposal, state.ingredients, state.recipes);
  if (rows.issues.length) return { issues: rows.issues, newIngredients: rows.ingredientStubs.map((item) => item.name), cost: undefined };
  const virtual = rows.ingredientStubs.map((item, index): Ingredient => ({ ...item, ingredientId: `preview-${index}`, tenantId: TENANT_ID, updatedAt: '', priceEstimated: item.purchaseUnit ? demoPriceEstimate(item.name, item.purchaseUnit, state.tenant.country, state.tenant.currency) ?? undefined : undefined }));
  const row = rows.recipeRows[0];
  const recipe: Recipe = { ...row, tenantId: TENANT_ID, recipeId: 'preview-demo', updatedAt: '', components: row.components.map((line) => ({ ...line, ref: line.ref.kind === 'ingredient_del_fichero' ? { kind: 'ingredient', ingredientId: virtual.find((item) => item.name === (line.ref as { ingredientName: string }).ingredientName)!.ingredientId } : line.ref })) };
  return { issues: rows.issues, newIngredients: virtual.map((item) => item.name), cost: computeRecipeCostPartial(recipe.recipeId, buildContext([...state.ingredients, ...virtual], [...state.recipes, recipe])) };
}

/* ------------------------------------------------------------------ */
/* Adaptador                                                           */
/* ------------------------------------------------------------------ */

let demoWelcomeEnabled = true;
let demoWeeklyEnabled = true;
export const demoApi: AlexiaRestApi = {
  async trialExtension() { return {enabled:false,usage:{dictations:0,scans:0},deliveries:[]}; },
  async acceptTrialExtension() { throw new Error('La demo no activa extensiones.'); },
  async adminTrialExtension() { return {enabled:false,usage:{dictations:0,scans:0},deliveries:[]}; },
  async adminOfferTrialExtension() { throw new Error('La demo no envía ofertas.'); },
  async welcomeEmailPreference() { return { enabled: demoWelcomeEnabled, consent: demoWelcomeEnabled }; },
  async setWelcomeEmailPreference(enabled) { demoWelcomeEnabled = enabled; return { enabled, consent: enabled }; },
  async markWelcomeResultViewed() { return { ok: true }; },
  async adminWelcomeHistory() { return { items: [], copyReady: false, sendingEnabled: false }; },
  async weeklyReportPreference() { return { enabled: demoWeeklyEnabled }; },
  async setWeeklyReportPreference(enabled) { demoWeeklyEnabled = enabled; return { enabled }; },
  async getPushSettings() { return { publicKey: null, subscribed: false, preferences: { enabled: true, localHour: 16 } }; },
  async savePushSubscription(_subscription, remove) { return { subscribed: !remove }; },
  async getInboundEmailSettings() {
    // Espejo de routes/inboundEmail.ts: sin escáner no hay dirección.
    if (!puedeEscanear(state.tenant.plan, state.tenant.addons ?? [])) return { available: false, configured: false, canChangeSlug: false, allowedSenders: state.tenant.inboundAllowedSenders ?? [], message: inboundEmailSinEscanerMessage(PLANS[state.tenant.plan]?.name ?? state.tenant.plan) };
    return { available: true, configured: false, address: `facturas-${state.tenant.inboundEmailSlug ?? 'demo-restaurante'}@facturas.alexiarest.com`, slug: state.tenant.inboundEmailSlug ?? 'demo-restaurante', canChangeSlug: false, allowedSenders: state.tenant.inboundAllowedSenders ?? [] };
  },
  async setInboundEmailAddress(slug) {
    if (slug) {
      if (state.tenant.inboundEmailSlugChangedAt) throw new Error('La dirección solo se puede cambiar una vez.');
      if (!/^[a-z0-9][a-z0-9-]{4,30}$/.test(slug)) throw new Error('Usa entre 5 y 31 letras minúsculas, números o guiones.');
      state.tenant.inboundEmailSlug = slug; state.tenant.inboundEmailSlugChangedAt = new Date().toISOString();
    }
    return { address: `facturas-${state.tenant.inboundEmailSlug ?? 'demo-restaurante'}@facturas.alexiarest.com`, slug: state.tenant.inboundEmailSlug ?? 'demo-restaurante' };
  },
  async setInboundEmailSenders(emails) { state.tenant.inboundAllowedSenders = emails; return { allowedSenders: emails }; },
  async getQuestions() {
    const ingredients = state.ingredients.map((item, index) => index < 3 && !answeredDemoQuestions.has(`price-${item.ingredientId}`) ? { ...item, pricePending: true, priceEstimated: { unitPrice: (item.netPrice ?? item.prices?.[0] ?? 1) / (item.purchaseQty ?? 1), unit: item.purchaseUnit ?? 'kg' as const, basis: 'alexia' as const, at: '2026-09-14' } } : item);
    const completeness = catalogCompleteness(state.recipes, ingredients, state.menuItems);
    return { questions: nextQuestions(state.tenant, { ingredients, recipes: state.recipes, menuItems: state.menuItems, history: questionHistory }, completeness, 1), completeness };
  },
  async skipQuestion(id) { questionHistory[id] = skipQuestion(questionHistory[id]); },
  async answerQuestion(id, answer) {
    const question = (await this.getQuestions()).questions.find((item) => item.id === id);
    if (!question) throw notFound('esa pregunta');
    if (question.kind === 'confirm-price') {
      const ingredient = state.ingredients.find((item) => item.ingredientId === question.ingredientId)!;
      const value = answer.unitPrice ?? question.unitPrice;
      if (!value || value <= 0) throw new Error('Escribe un precio mayor que cero.');
      await this.updateIngredient(ingredient.ingredientId, { ...catalogPriceInput(ingredient, ingredient.name, value, 1, purchaseUnitSchema.parse(question.unit)), priceSource: 'manual' });
      answeredDemoQuestions.add(id);
    }
    return {};
  },
  async getWhatsAppSettings() { return { configured: false }; },
  async requestWhatsAppCode() { throw new Error('La demo no envía mensajes de WhatsApp.'); },
  async verifyWhatsAppCode() { throw new Error('La demo no vincula teléfonos.'); },
  async unlinkWhatsApp() { return { unlinked: true }; },
  async updateWhatsAppPreferences(input) { return input; },
  // En demo no hay onboarding real: el restaurante de ejemplo ya existe.
  async onboard() {
    return { tenant: clone(state.tenant), tokensStale: false };
  },

  // Las fotos en demo viajan como dataURL dentro de la receta (PhotoField
  // no llama a estos metodos en modo demo).
  async createPhotoUploadUrl(): Promise<never> {
    throw new Error('Las fotos en la demo se guardan dentro de la receta.');
  },
  async getPhotoUrl(): Promise<never> {
    throw new Error('Las fotos en la demo se guardan dentro de la receta.');
  },

  async transcribeRecipeAudio(input) {
    const voiceApi = (import.meta.env.VITE_VOICE_API_URL ?? '').trim().replace(/\/+$/, '');
    if (voiceApi) {
      const response = await fetch(`${voiceApi}/v1/recipes/transcribe`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      });
      const payload = await response.json() as { text?: string; error?: { message?: string } };
      if (!response.ok || !payload.text) {
        throw new ApiError(
          'INTERNAL',
          payload.error?.message ?? 'No he podido escuchar la grabación.',
          response.status,
        );
      }
      return { text: payload.text };
    }
    await new Promise((resolve) => setTimeout(resolve, 700));
    return {
      text: 'Risotto de setas para cuatro personas: 320 gramos de arroz carnaroli, medio litro de caldo de verduras y 200 gramos de boletus.',
    };
  },

  async adminGrowthTarget() { return { monthly: 0 }; },
  async adminSaveGrowthTarget(monthly) { return { monthly }; },
  async getMe(): Promise<MeResponse> {
    // La demo es el escaparate para restauradores: el panel de plataforma
    // (clientes, MRR) es informacion NUESTRA y no pinta nada aqui.
    return {
      user: state.user,
      tenant: state.tenant,
      memberships: [state.user],
      locations: [demoLocation],
    };
  },

  async listLocations() {
    return [demoLocation];
  },
  async createLocation(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'La demo solo incluye el centro Principal.', 403);
  },
  async updateLocation(_locationId, input) {
    demoLocation = { ...demoLocation, ...input };
    return demoLocation;
  },

  async updateTenant(patch: TenantUpdate): Promise<Tenant> {
    const { setup, ...resto } = patch;
    state.tenant = { ...state.tenant, ...resto };
    // Igual que la API: un WhatsApp vacio se borra, no se guarda "".
    if (!state.tenant.whatsapp?.trim()) delete state.tenant.whatsapp;
    // Cuestionario inicial: en la demo se puede repetir desde Ayuda y Alex
    // lo graba asi. Misma regla que el servidor: completed pone la fecha.
    if (setup) {
      state.tenant.setup = setup.completed
        ? { step: setup.step, firstRecipeId: setup.firstRecipeId ?? state.tenant.setup?.firstRecipeId, recipeFirstPending: false, completedAt: new Date().toISOString() }
        : { step: setup.step, firstRecipeId: setup.firstRecipeId ?? state.tenant.setup?.firstRecipeId, recipeFirstPending: setup.recipeFirstPending ?? state.tenant.setup?.recipeFirstPending };
    }
    return state.tenant;
  },

  async markTourSeen() {
    const tourSeenAt = new Date().toISOString();
    state.user = { ...state.user, tourSeenAt };
    return { tourSeenAt };
  },

  /* --- Ajustes --- */

  async exportBusiness(): Promise<Record<string, object[]>> {
    return clone({Ingredientes:state.ingredients, Recetas:state.recipes, Carta:state.menuItems, Proveedores:state.suppliers, Ingresos:state.revenues, Gastos:state.expenses, Inversiones:state.investments, Tesorería:state.cashMovements, Inventarios:state.inventoryCounts, Objetivos:state.targets, Ajustes:[state.settings], Locales:[{locationId:'principal', name:'Principal', active:true}], Restaurante:[{nombre:state.tenant.name, moneda:state.tenant.currency, país:state.tenant.country, datosFiscales:state.tenant.tax}]});
  },
  async getSettings(): Promise<TenantSettings> {
    return clone(state.settings);
  },

  async updateSettings(input: TenantSettings): Promise<TenantSettings> {
    state.settings = clone(input);
    state.tenant = { ...state.tenant, settings: state.settings };
    return clone(state.settings);
  },

  /* --- Integraciones con TPV: la demo no se conecta con nada --- */

  async listPosIntegrations(): Promise<PosIntegrationsResponse> {
    const none = { oauth: false, token: false };
    return { items: [], providers: { loyverse: { oauth: true, token: true }, square: { oauth: true, token: false }, revo: none, glop: { oauth: false, token: true }, lastapp: none, restaurantpe: { oauth: false, token: true }, bistrosoft: { oauth: false, token: true }, polotab: { oauth: false, token: true }, sumup: { oauth: false, token: true } } };
  },
  async startPosConnection(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'La demo no se conecta con ningún TPV. Crea tu cuenta para conectar el tuyo.', 403);
  },
  async completePosConnection(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'La demo no se conecta con ningún TPV.', 403);
  },
  async connectLoyverseToken(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'La demo no se conecta con ningún TPV. Crea tu cuenta para conectar el tuyo.', 403);
  },
  async connectGlopCredentials(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'La demo no se conecta con ningún TPV. Crea tu cuenta para conectar el tuyo.', 403);
  },
  async connectRestaurantPeCredentials(): Promise<never> { throw new Error('La demo no admite conexiones con TPV.'); },
  async connectPosCredentials(): Promise<never> { throw new Error('La demo no admite conexiones con TPV.'); },
  async connectLastAppAccount(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'La demo no se conecta con ningún TPV. Crea tu cuenta para conectar el tuyo.', 403);
  },
  async updatePosIntegration(): Promise<never> {
    throw new ApiError('NOT_FOUND', 'En la demo no hay ningún TPV conectado.', 404);
  },
  async syncPosSales(): Promise<never> {
    throw new ApiError('NOT_FOUND', 'En la demo no hay ningún TPV conectado.', 404);
  },
  async applyPosUnits(): Promise<never> {
    throw new ApiError('NOT_FOUND', 'En la demo no hay ningún TPV conectado.', 404);
  },
  async disconnectPos(): Promise<void> {
    return;
  },

  /* --- Ingredientes --- */

  async listInbox() {
    return clone(inbox.map((entry) => ({ ...entry, cards: entry.cards.map((card) => ({ ...card, draft: drafts.find((d) => d.id === card.draft.id) ?? card.draft })) })));
  },
  async sendInbox(input) {
    const previous = (await this.listInbox()).find((entry) => entry.id === input.id);
    if (previous) return previous;
    // La demo usa ejemplos locales; nunca envía el texto a un servicio externo.
    // Una pregunta (o algo que no parece una receta ni un gasto) se responde
    // con el mismo motor determinista que el panel de ayuda: datos reales
    // del dataset, sin modelo detrás. Lo demás se enseña con el risotto.
    const pregunta = !input.draftIds && !input.replyToDraftId && /[¿?]|^(qué|que|cómo|como|cuánto|cuanto|dónde|donde|por qué|porque|analiza|dime|explíca|explica)\b/i.test(input.text.trim());
    if (pregunta) {
      await pause(400);
      const answer = demoChat(assistantData(), input.text, input.period ?? demoPeriod, todayIso());
      const entry: InboxEntry = { ...input, createdAt: new Date().toISOString(), expiresAt: Math.floor(Date.now() / 1000) + DRAFT_TTL_SECONDS, reply: answer.reply, replyKind: 'analysis', toolCalls: answer.toolCalls, cards: [] };
      inbox.push(entry); return clone(entry);
    }
    const ids = input.draftIds ?? [(await this.createRecipeDraft(structuredClone(RECIPE_FIXTURES[0].proposals[0]))).id];
    const cards = await Promise.all(ids.map(async (id) => {
      const draft = await this.getDraft(id);
      const preview = draft.kind === 'recipe' ? previewDemoRecipe(draft.payload) : undefined;
      const money = (n: number) => new Intl.NumberFormat(state.tenant.locale, { style: 'currency', currency: state.tenant.currency }).format(n);
      const text = draft.kind === 'recipe' ? `${draft.payload.name} · ${draft.payload.servings} raciones · ${draft.payload.lines.length} ingredientes.${preview?.cost ? ` Coste por ración ${preview.cost.costEstimated ? '~' : ''}${money(preview.cost.costPerPortion)}${preview.cost.costEstimated ? ' (estimado)' : ''}.` : ''}` : draft.kind === 'expense' ? `${draft.summary} · Base ${money(draft.payload.proposal.netAmount)} + IVA ${ivaEnFrase(draft.payload.proposal)}.` : draft.summary;
      return { draft, text };
    }));
    const entry: InboxEntry = { ...input, draftIds: ids, createdAt: new Date().toISOString(), expiresAt: Math.floor(Date.now() / 1000) + DRAFT_TTL_SECONDS, reply: input.fileName ? 'He preparado estas propuestas de ejemplo. Confirma el gasto y los precios por separado.' : 'En esta demo te muestro un risotto de ejemplo. En tu restaurante interpretaré lo que me cuentes.', replyKind: 'proposals', cards };
    inbox.push(entry); return clone(entry);
  },
  async listDrafts(locationId) {
    return clone(drafts.filter((draft) => draft.status === 'pending' && !draftIsExpired(draft) && (!locationId || (draft.locationId ?? 'principal') === locationId)));
  },
  async getDraft(id) {
    const draft = drafts.find((item) => item.id === id);
    if (!draft) throw notFound('ese borrador');
    if (draft.status === 'pending' && draftIsExpired(draft)) throw new ApiError('GONE', 'El borrador ha caducado.', 410);
    return clone(draft);
  },
  async createRecipeDraft(payload) {
    const now = new Date();
    const draft: Draft = { id: newId('draft'), tenantId: TENANT_ID, kind: 'recipe', source: 'demo', status: 'pending', payload: clone(payload), summary: payload.name, createdBy: { userSub: state.user.userId }, createdAt: now.toISOString(), expiresAt: Math.floor(now.getTime() / 1000) + DRAFT_TTL_SECONDS };
    drafts.push(draft);
    return clone(draft);
  },
  async createDraftLink(id) {
    await this.getDraft(id);
    const token = newId('link');
    const expiresAt = Math.floor(Date.now() / 1000) + 900;
    draftLinks.set(token, { id, expiresAt });
    return { url: `${window.location.origin}/borradores/${id}?token=${token}`, expiresAt };
  },
  async redeemDraftLink(id, token) {
    const link = draftLinks.get(token);
    if (!link || link.id !== id || link.expiresAt <= Date.now() / 1000) throw new ApiError('GONE', 'El enlace ha caducado o ya se usó.', 410);
    draftLinks.delete(token);
    return { redeemed: true };
  },
  async discardDraft(id) {
    const draft = await this.getDraft(id);
    if (draft.status === 'confirmed') throw new ApiError('CONFLICT', 'El borrador ya se confirmó.', 409);
    const discarded = { ...draft, status: 'discarded' as const };
    drafts = drafts.map((item) => item.id === id ? discarded : item);
    return clone(discarded);
  },
  async confirmDraft(id, patch = {}) {
    const running = confirmingDrafts.get(id);
    if (running) return running;
    const task = (async () => {
      const draft = await this.getDraft(id);
      if (draft.status === 'confirmed') return draft;
      if (draft.status !== 'pending') throw new ApiError('CONFLICT', 'El borrador ya está descartado.', 409);
      let result: Draft['result'];
      if (draft.kind === 'recipe') {
        const input = patch.recipe ?? { ...draft.payload, servings: draft.payload.servings ?? 1, lines: draft.payload.lines.map((line) => {
          const matches = line.isSubrecipe ? state.recipes.filter((recipe) => normalizeIngredientName(recipe.name) === normalizeIngredientName(line.ingredientName)) : [];
          if (line.isSubrecipe && matches.length !== 1) throw new Error('Revisa la subreceta en la app.');
          return { ingredientName: line.ingredientName, ingredientId: line.ingredientId, recipeId: matches[0]?.recipeId, qty: line.qty ?? 0, unit: line.unit ?? 'g', wastePct: line.wastePct ?? 0 };
        }) };
        const saved = await this.confirmRecipeProposal(input);
        result = { recipeId: saved.recipe.recipeId, ingredientIds: saved.newIngredients.map((item) => item.ingredientId) };
      } else if (draft.kind === 'expense') {
        if (draft.payload.proposal.duplicado && !patch.acceptPossibleDuplicate) throw new ApiError('CONFLICT', 'La factura ya está guardada.', 409);
        const input = patch.expense ?? draft.payload.input ?? { ...draft.payload.proposal, notes: draft.payload.proposal.vatNote };
        if (!patch.acceptPossibleDuplicate && buscarDuplicado(input, state.expenses, draft.payload.documentType ?? 'factura')) throw new Error('Esta factura ya está guardada. Revísala antes de confirmar.');
        const saved = await this.createExpense(input);
        result = { expenseId: saved.entryId };
      } else if (draft.kind === 'catalog-prices') {
        if (!patch.prices && draft.payload.lines.some((line) => line.action === 'choose')) throw new Error('Revisa las coincidencias dudosas.');
        const prices = patch.prices ?? draft.payload.lines.filter((line) => line.action !== 'ignore').map((line) => ({ ingredientId: ['exact', 'fuzzy'].includes(line.match.kind) ? line.match.candidates[0]?.ingredientId : undefined, name: line.name, price: line.unitPrice!, qty: 1, unit: purchaseUnitSchema.parse(line.unit) }));
        if (!prices.length) throw new Error('No hay precios para confirmar.');
        const batch = { items: prices.map((row) => {
          const current = state.ingredients.find((item) => item.ingredientId === row.ingredientId);
          if (!current && (row.ingredientId || !('createNew' in row) || !row.createNew)) throw new Error('Elige un ingrediente existente o confirma la creación.');
          const input = { ...catalogPriceInput(current, current?.name ?? row.name, row.price, row.qty, row.unit), ...(!current && 'createNew' in row ? { presentation: row.presentation, supplierId: row.supplierId } : {}) };
          if (!current && state.ingredients.some(ing => invoiceIngredientKey(ing) === invoiceIngredientKey(input))) throw new Error('Este ingrediente y formato ya existen. Selecciónalo para actualizar su precio.');
          return { id: current?.ingredientId, input };
        }) };
        const saved = await this.importIngredients(batch);
        if (saved.errores.length) throw new ApiError('VALIDATION_ERROR', saved.errores.map((error) => error.message).join(' · '), 400);
        result = {};
      } else throw new ApiError('VALIDATION_ERROR', 'Revisa los precios antes de guardar.', 400);
      const confirmed = { ...draft, status: 'confirmed' as const, confirmedAt: new Date().toISOString(), result };
      drafts = drafts.map((item) => item.id === id ? confirmed : item);
      return clone(confirmed);
    })();
    confirmingDrafts.set(id, task);
    try { return await task; } finally { confirmingDrafts.delete(id); }
  },

  async estimateMissingPrices() {
    let estimated = 0;
    const errors: Array<{ ingredientId: string; message: string }> = [];
    state.ingredients = state.ingredients.map((item) => {
      if (netPriceOf(item) || item.priceEstimated) return item;
      const priceEstimated = item.purchaseUnit ? demoPriceEstimate(item.name, item.purchaseUnit, state.tenant.country, state.tenant.currency) : null;
      if (!priceEstimated) { errors.push({ ingredientId: item.ingredientId, message: 'Sin estimación disponible. Completa este precio.' }); return item; }
      estimated += 1;
      return { ...item, priceEstimated, pricePending: true };
    });
    return { estimated, remaining: errors.length, errors };
  },

  async listPresentations() { return clone(purchasePresentations); },
  async createPresentation(name: string) {
    const normalized = name.normalize('NFC').trim();
    if (!normalized || normalized.length > 120) throw new Error('La presentación debe tener entre 1 y 120 caracteres.');
    const values = purchasePresentations;
    const existing = values.find(value => value.toLocaleLowerCase('es') === normalized.toLocaleLowerCase('es'));
    if (existing) return { name: existing };
    purchasePresentations = [...values, normalized];
    return { name: normalized };
  },
  async listIngredients(): Promise<Ingredient[]> {
    return [...state.ingredients].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  },

  async createIngredient(input: IngredientInput): Promise<Ingredient> {
    const created: Ingredient = {
      ...input,
      tenantId: TENANT_ID,
      pricePending: input.netPrice == null,
      priceEstimated: input.netPrice == null && input.purchaseUnit ? demoPriceEstimate(input.name, input.purchaseUnit, state.tenant.country, state.tenant.currency) ?? undefined : undefined,
      ingredientId: newId('ing'),
      updatedAt: new Date().toISOString(),
    };
    state.ingredients = [...state.ingredients, created];
    return created;
  },

  async updateIngredient(id: string, input: IngredientInput): Promise<Ingredient> {
    const current = state.ingredients.find((i) => i.ingredientId === id);
    if (!current) throw notFound('ese ingrediente');
    const updated: Ingredient = {
      ...current,
      ...input,
      pricePending: input.netPrice == null,
      priceEstimated: current.priceEstimated,
      allergens: input.allergens ?? current.allergens,
      priceSource: input.priceSource ?? (input.netPrice !== current.netPrice ? 'manual' : current.priceSource),
      ingredientId: id,
      tenantId: TENANT_ID,
      updatedAt: new Date().toISOString(),
    };
    state.ingredients = state.ingredients.map((i) => (i.ingredientId === id ? updated : i));
    return updated;
  },

  async renameIngredient(id: string, name: string): Promise<Ingredient> {
    const current = state.ingredients.find(i => i.ingredientId === id);
    if (!current) throw notFound('ese ingrediente');
    const updated = { ...current, name: name.trim(), updatedAt: new Date().toISOString() };
    state.ingredients = state.ingredients.map(i => i.ingredientId === id ? updated : i);
    return updated;
  },

  async deleteIngredient(id: string): Promise<void> {
    // Misma red y mismo mensaje que la API: shared/recipeUsage.ts.
    const aviso = ingredientUsageMessage(ingredientUsage(id, state.recipes));
    if (aviso) throw new ApiError('CONFLICT', aviso, 409);
    state.ingredients = state.ingredients.filter((i) => i.ingredientId !== id);
  },

  /* --- Proveedores --- */

  async listSuppliers(): Promise<Supplier[]> {
    return [...state.suppliers];
  },

  async createSupplier(input: SupplierInput): Promise<Supplier> {
    const created: Supplier = { ...input, tenantId: TENANT_ID, supplierId: newId('sup') };
    state.suppliers = [...state.suppliers, created];
    return created;
  },

  async updateSupplier(id: string, input: SupplierInput): Promise<Supplier> {
    const current = state.suppliers.find((s) => s.supplierId === id);
    if (!current) throw notFound('ese proveedor');
    const updated: Supplier = { ...current, ...input };
    state.suppliers = state.suppliers.map((s) => (s.supplierId === id ? updated : s));
    return updated;
  },

  async deleteSupplier(id: string): Promise<void> {
    state.suppliers = state.suppliers.filter((s) => s.supplierId !== id);
  },

  /* --- Recetas --- */

  async listRecipes(): Promise<Recipe[]> {
    return [...state.recipes].sort((a, b) => a.name.localeCompare(b.name, 'es'));
  },

  async getRecipe(id: string): Promise<Recipe> {
    const recipe = state.recipes.find((r) => r.recipeId === id);
    if (!recipe) throw notFound('esa receta');
    return recipe;
  },

  async createRecipe(input: RecipeInput): Promise<Recipe> {
    const created: Recipe = {
      ...input,
      tenantId: TENANT_ID,
      recipeId: newId('rec'),
      updatedAt: new Date().toISOString(),
    };
    state.recipes = [...state.recipes, created];
    return created;
  },

  async updateRecipe(id: string, input: RecipeInput): Promise<Recipe> {
    const current = state.recipes.find((r) => r.recipeId === id);
    if (!current) throw notFound('esa receta');
    const updated: Recipe = {
      ...current,
      ...input,
      recipeId: id,
      tenantId: TENANT_ID,
      updatedAt: new Date().toISOString(),
    };
    state.recipes = state.recipes.map((r) => (r.recipeId === id ? updated : r));
    return updated;
  },

  async deleteRecipe(id: string): Promise<void> {
    // Misma red y mismo mensaje que la API (mejora 50): shared/recipeUsage.ts.
    const aviso = recipeUsageMessage(recipeUsage(id, state.recipes, state.menuItems));
    if (aviso) throw new ApiError('CONFLICT', aviso, 409);
    state.recipes = state.recipes.filter((r) => r.recipeId !== id);
  },

  async getRecipeCost(id: string): Promise<RecipeCost> {
    if (!state.recipes.some((r) => r.recipeId === id)) throw notFound('esa receta');
    return computeRecipeCostPartial(id, context());
  },

  async getRecipeCosts(): Promise<RecipeCostsResponse> {
    const ctx = context();
    return {
      items: state.recipes.map((recipe) => {
        try {
          return { recipeId: recipe.recipeId, cost: computeRecipeCostPartial(recipe.recipeId, ctx) };
        } catch (error) {
          return {
            recipeId: recipe.recipeId,
            error: {
              message:
                error instanceof Error
                  ? error.message
                  : 'No hemos podido costear esta receta.',
              culprit:
                error instanceof CostingError || error instanceof UnitError
                  ? error.culprit
                  : undefined,
            },
          };
        }
      }),
    };
  },

  async proposeRecipeFromFile(_file: Blob): Promise<RecipeProposalResponse> {
    const proposal = RECIPE_FIXTURES[0].proposals[0];
    return { draftId: (await this.createRecipeDraft(proposal)).id, proposal, plan: planRecipeProposal(proposal, state.ingredients), rows: proposalToImportRows(proposal, state.ingredients, state.recipes), catalog: state.ingredients, recipeCatalog: state.recipes };
  },

  async proposeRecipe(text: string): Promise<RecipeProposalResponse> {
    const voiceApi = (import.meta.env.VITE_VOICE_API_URL ?? '').trim().replace(/\/+$/, '');
    if (voiceApi) {
      const response = await fetch(`${voiceApi}/v1/recipes/proposal`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      const payload = await response.json() as RecipeProposalResponse & {
        error?: { message?: string };
      };
      if (!response.ok || !payload.proposal) {
        throw new ApiError(
          'INTERNAL',
          payload.error?.message ?? 'No he podido interpretar la receta.',
          response.status,
        );
      }
      return payload;
    }
    const proposal = RECIPE_FIXTURES[0].proposals[0];
    return {
      draftId: (await this.createRecipeDraft(proposal)).id,
      proposal,
      plan: planRecipeProposal(proposal, state.ingredients),
      rows: proposalToImportRows(proposal, state.ingredients, state.recipes),
      recipeCatalog: state.recipes.map(({ recipeId, name }) => ({ recipeId, name })),
      catalog: state.ingredients.map(({ ingredientId, name, purchaseUnit }) => ({ ingredientId, name, purchaseUnit })),
    };
  },

  async confirmRecipeProposal(input: ConfirmRecipeProposalInput): Promise<ConfirmRecipeProposalResponse> {
    const rows = proposalToImportRows({ ...input, lines: input.lines.map((line) => ({
      ...line, rawText: line.ingredientName, isSubrecipe: Boolean(line.recipeId), estimated: { qty: line.estimated },
    })) }, state.ingredients, state.recipes, Object.fromEntries(input.lines.map((line, index) => [index, line])));
    if (rows.issues.length) throw new ApiError('VALIDATION_ERROR', rows.issues.map((issue) => issue.message).join(' · '), 400);
    const beforeIngredients = new Set(state.ingredients.map((ing) => ing.ingredientId));
    const beforeRecipes = new Set(state.recipes.map((recipe) => recipe.recipeId));
    if (rows.ingredientStubs.length) {
      const added = await this.importIngredients({ items: rows.ingredientStubs.map((input) => ({ input })) });
      if (added.errores.length) throw new ApiError('VALIDATION_ERROR', added.errores.map((error) => error.message).join(' · '), 400);
    }
    const saved = await this.importRecipes({ items: rows.recipeRows.map((row) => ({ id: input.recipeId, input: row })) });
    if (saved.errores.length) throw new ApiError('VALIDATION_ERROR', saved.errores.map((error) => error.message).join(' · '), 400);
    const recipe = state.recipes.find((recipe) => input.recipeId ? recipe.recipeId === input.recipeId : !beforeRecipes.has(recipe.recipeId))!;
    return { recipe, newIngredients: state.ingredients.filter((ing) => !beforeIngredients.has(ing.ingredientId)) };
  },

  /* --- Carta y precios --- */

  async listMenuItems(): Promise<MenuItem[]> {
    return [...state.menuItems];
  },

  async createMenuItem(input: MenuItemInput): Promise<MenuItem> {
    const created: MenuItem = {
      ...input,
      tenantId: TENANT_ID,
      menuItemId: newId('menu'),
      updatedAt: new Date().toISOString(),
    };
    // El coste teorico se fija solo, con el coste del dia en que se creo el
    // plato. Nadie lo teclea a mano.
    if (created.theoreticalCost === undefined) {
      try {
        created.theoreticalCost = computeRecipeCost(created.recipeId, context()).costPerPortion;
        created.theoreticalCostAt = created.updatedAt;
        created.theoreticalCostIngredients = ingredientCostShares(created.recipeId, state.ingredients, state.recipes);
      } catch {
        // receta sin costear: la referencia llegara al primer "aceptar coste"
      }
    }
    state.menuItems = [...state.menuItems, created];
    return created;
  },

  async acceptMenuItemCost(id: string): Promise<MenuItem> {
    const current = state.menuItems.find((m) => m.menuItemId === id);
    if (!current) throw notFound('ese plato de la carta');
    const cost = computeRecipeCost(current.recipeId, context());
    const updated: MenuItem = {
      ...current,
      theoreticalCostIngredients: ingredientCostShares(current.recipeId, state.ingredients, state.recipes),
      theoreticalCost: cost.costPerPortion,
      theoreticalCostAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    state.menuItems = state.menuItems.map((m) => (m.menuItemId === id ? updated : m));
    return updated;
  },

  async updateMenuItem(id: string, input: MenuItemInput): Promise<MenuItem> {
    const current = state.menuItems.find((m) => m.menuItemId === id);
    if (!current) throw notFound('ese plato de la carta');
    const updated: MenuItem = {
      ...current,
      ...input,
      menuItemId: id,
      tenantId: TENANT_ID,
      updatedAt: new Date().toISOString(),
    };
    state.menuItems = state.menuItems.map((m) => (m.menuItemId === id ? updated : m));
    return updated;
  },

  async updateMenuItemUnitsSold(id: string, unitsSold: number, locationId?: string): Promise<MenuItem> {
    const current = state.menuItems.find((m) => m.menuItemId === id);
    if (!current) throw notFound('ese plato de la carta');
    const updated = {
      ...(locationId
        ? withUnitsForLocation(current, locationId, unitsSold)
        : { ...current, unitsSold, unitsSoldByLocation: undefined }),
      updatedAt: new Date().toISOString(),
    };
    state.menuItems = state.menuItems.map((m) => (m.menuItemId === id ? updated : m));
    return updated;
  },

  async deleteMenuItem(id: string): Promise<void> {
    state.menuItems = state.menuItems.filter((m) => m.menuItemId !== id);
  },

  async getPricing(locationId?: string): Promise<PricingResponse> {
    const { rows, skipped } = pricingItems();
    return {
      items: rows.map((r) => r.analysis),
      engineering: menuEngineering(
        rows.map(({ item, analysis }) => ({
          item: locationId ? { ...item, unitsSold: unitsSoldFor(item, locationId) } : item,
          analysis,
          // El grupo de carta vive en la receta: la matriz clasifica por grupo.
          group: state.recipes.find((r) => r.recipeId === item.recipeId)?.group ?? null,
        })),
      ),
      skipped,
    };
  },

  async getPlatformComparison(id: string): Promise<PricingAnalysis[]> {
    const item = state.menuItems.find((m) => m.menuItemId === id);
    if (!item) throw notFound('ese plato de la carta');
    return comparePlatforms(item, costOf(item.recipeId), state.settings);
  },

  async getCostDeviations(): Promise<CostDeviation[]> {
    const rows = pricingItems().rows.map(({ item, cost }) => ({ item, cost }));
    return findCostDeviations(rows, state.settings);
  },

  /* --- Ingresos diarios --- */

  async listRevenues(period: Period): Promise<DailyRevenue[]> {
    return state.revenues
      .filter((d) => inPeriod(d.date, period))
      .sort((a, b) => a.date.localeCompare(b.date));
  },

  async saveRevenue(input: DailyRevenueInput): Promise<DailyRevenue> {
    const saved: DailyRevenue = {
      ...input,
      tenantId: TENANT_ID,
      updatedAt: new Date().toISOString(),
    };
    const exists = state.revenues.some((d) => d.date === input.date);
    state.revenues = exists
      ? state.revenues.map((d) => (d.date === input.date ? saved : d))
      : [...state.revenues, saved];
    return saved;
  },

  async deleteRevenue(date: string): Promise<void> {
    state.revenues = state.revenues.filter((d) => d.date !== date);
  },

  /* --- Gastos --- */

  async listExpenses(period: Period): Promise<ExpenseEntry[]> {
    return state.expenses
      .filter((e) => inPeriod(e.date, period))
      .sort((a, b) => b.date.localeCompare(a.date));
  },

  async getExpense(id: string): Promise<ExpenseEntry> {
    const found = state.expenses.find((e) => e.entryId === id);
    if (!found) throw new Error('Ese gasto ya no existe.');
    return found;
  },

  async listReconciliationCandidates(invoiceId: string): Promise<ExpenseEntry[]> {
    const invoice = state.expenses.find((expense) => expense.entryId === invoiceId);
    if (!invoice) throw notFound('la factura');
    // Igual que la API: sin proveedor no hay emparejamiento posible.
    return state.expenses.filter((expense) =>
      expense.documentType === 'albaran' &&
      !expense.reconciledInvoiceId &&
      (!expense.supplierId || (invoice.supplierId ? expense.supplierId === invoice.supplierId : false))
    );
  },

  async importExpenses(items: ExpenseInput[]): Promise<{ created: number; skipped: number }> {
    // Igual que la API: un gasto ya importado no se crea otra vez.
    const { nuevos, repetidos } = separarRepetidos(
      items.map((input) => ({ input })),
      state.expenses,
    );
    for (const { input } of nuevos) await this.createExpense(input);
    return { created: nuevos.length, skipped: repetidos.length };
  },

  async createExpense(input: ExpenseInput): Promise<ExpenseEntry> {
    // Igual que la API: una nota de venta nace pendiente de factura; el pago
    // lo dice quien la registra.
    const created: ExpenseEntry = {
      ...input,
      ...(input.documentType === 'albaran' ? { invoiceStatus: 'pendiente' as const } : {}),
      tenantId: TENANT_ID,
      entryId: newId('exp'),
    };
    state.expenses = [...state.expenses, created];
    return created;
  },

  async updateExpense(id: string, input: ExpenseInput): Promise<ExpenseEntry> {
    const current = state.expenses.find((e) => e.entryId === id);
    if (!current) throw notFound('ese gasto');
    const reconciled = Boolean(current.reconciledInvoiceId || current.deliveryNoteIds?.length);
    const accountingChanged =
      current.date !== input.date ||
      current.netAmount !== input.netAmount ||
      current.supplierId !== input.supplierId ||
      (current.documentType ?? 'factura') !== (input.documentType ?? 'factura');
    if (reconciled && accountingChanged) {
      throw new Error('No se pueden cambiar los datos contables de un documento conciliado.');
    }
    const updated: ExpenseEntry = { ...current, ...input, entryId: id, tenantId: TENANT_ID };
    // Igual que la API: el estado de un albaran lo dicta su conciliacion.
    if ((updated.documentType ?? 'factura') === 'albaran') {
      updated.invoiceStatus = updated.reconciledInvoiceId ? 'facturado' : 'pendiente';
      if (updated.reconciledInvoiceId) updated.paid = true;
    }
    state.expenses = state.expenses.map((e) => (e.entryId === id ? updated : e));
    return updated;
  },

  async deleteExpense(id: string): Promise<void> {
    const current = state.expenses.find((e) => e.entryId === id);
    if (current && (current.reconciledInvoiceId || current.deliveryNoteIds?.length)) {
      throw new Error('No se puede borrar un documento conciliado: forma parte del histórico de la compra.');
    }
    state.expenses = state.expenses.filter((e) => e.entryId !== id);
  },

  async reconcileExpense(invoiceId: string, deliveryNoteIds: string[], marcarFacturaPagada?: boolean): Promise<ReconciliationResult> {
    if (new Set(deliveryNoteIds).size !== deliveryNoteIds.length) {
      throw new Error('No se puede repetir un mismo albarán.');
    }
    const invoice = state.expenses.find((e) => e.entryId === invoiceId);
    const deliveryNotes = state.expenses.filter((e) => deliveryNoteIds.includes(e.entryId));
    if (!invoice || deliveryNotes.length !== deliveryNoteIds.length) throw notFound('alguno de los documentos');
    const totalAlbaranes = Math.round(deliveryNotes.reduce((sum, e) => sum + e.netAmount, 0) * 100) / 100;
    const difference = Math.round((invoice.netAmount - totalAlbaranes) * 100) / 100;
    const reconciliationStatus = Math.abs(difference) < 0.01 ? 'conciliado' as const : 'con_diferencias' as const;
    const reconciledAt = new Date().toISOString();
    const pagar = Boolean(marcarFacturaPagada) && !invoice.paid;
    // Igual que la API: el proveedor de la compra es el de la factura o, si no
    // lo tiene, el unico de sus notas.
    const proveedorDeLaCompra = invoice.supplierId ?? deliveryNotes.map((n) => n.supplierId).find(Boolean);
    const nextInvoice = {
      ...invoice,
      supplierId: proveedorDeLaCompra,
      deliveryNoteIds,
      reconciliationStatus,
      reconciliationDifference: difference,
      reconciledAt,
      ...(pagar
        ? {
            paid: true,
            paymentDate: ultimaFechaDePago(deliveryNotes, reconciledAt.slice(0, 10)),
          }
        : {}),
    };
    state.expenses = state.expenses.map((e) => {
      if (e.entryId === invoiceId) return nextInvoice;
      // Igual que la API: el albaran conciliado sale de pendiente.
      if (deliveryNoteIds.includes(e.entryId)) return { ...e, supplierId: e.supplierId ?? proveedorDeLaCompra, reconciledInvoiceId: invoiceId, reconciliationStatus, reconciliationDifference: difference, reconciledAt, invoiceStatus: 'facturado' as const, paid: true };
      return e;
    });
    return { invoice: nextInvoice, deliveryNotes, totalAlbaranes, difference };
  },

  async unreconcileExpense(invoiceId: string): Promise<{ invoiceId: string; deliveryNotes: number }> {
    const invoice = state.expenses.find((e) => e.entryId === invoiceId);
    if (!invoice?.deliveryNoteIds?.length) throw new Error('Esta factura no está conciliada.');
    const ids = new Set(invoice.deliveryNoteIds);
    state.expenses = state.expenses.map((e) => {
      if (e.entryId === invoiceId) {
        const { deliveryNoteIds: _ids, reconciliationStatus: _s, reconciliationDifference: _d, reconciledAt: _a, ...resto } = e;
        return resto;
      }
      if (ids.has(e.entryId)) {
        const { reconciledInvoiceId: _i, reconciliationStatus: _s, reconciliationDifference: _d, reconciledAt: _a, ...resto } = e;
        return { ...resto, invoiceStatus: 'pendiente' as const };
      }
      return e;
    });
    return { invoiceId, deliveryNotes: ids.size };
  },

  async getPayables(today: string): Promise<PayablesResponse> {
    return payablesResponse(today || todayIso());
  },

  /* --- Inversiones --- */

  async listInvestments(): Promise<Investment[]> {
    return [...state.investments].sort((a, b) => a.date.localeCompare(b.date));
  },

  async createInvestment(input: InvestmentInput): Promise<Investment> {
    const created: Investment = { ...input, tenantId: TENANT_ID, investmentId: newId('inv') };
    state.investments = [...state.investments, created];
    return created;
  },

  async updateInvestment(id: string, input: InvestmentInput): Promise<Investment> {
    const current = state.investments.find((i) => i.investmentId === id);
    if (!current) throw notFound('esa inversión');
    const updated: Investment = {
      ...current,
      ...input,
      investmentId: id,
      tenantId: TENANT_ID,
    };
    state.investments = state.investments.map((i) => (i.investmentId === id ? updated : i));
    return updated;
  },

  async deleteInvestment(id: string): Promise<void> {
    state.investments = state.investments.filter((i) => i.investmentId !== id);
  },

  async getAmortizationSchedule(month: string): Promise<AmortizationRow[]> {
    return amortizationSchedule(state.investments, month || currentMonth());
  },

  /* --- Movimientos de caja --- */

  async listCashMovements(period: Period): Promise<CashMovement[]> {
    return state.cashMovements
      .filter((m) => inPeriod(m.date, period))
      .sort((a, b) => b.date.localeCompare(a.date));
  },

  async createCashMovement(input: CashMovementInput): Promise<CashMovement> {
    const created: CashMovement = { ...input, tenantId: TENANT_ID, movementId: newId('cash') };
    state.cashMovements = [...state.cashMovements, created];
    return created;
  },

  async deleteCashMovement(id: string): Promise<void> {
    state.cashMovements = state.cashMovements.filter((m) => m.movementId !== id);
  },

  /* --- Objetivos --- */

  async getTarget(month: string): Promise<Target | null> {
    return state.targets.find((t) => t.month === month) ?? null;
  },

  async saveTarget(month: string, input: TargetInput): Promise<Target> {
    const saved: Target = { ...input, month, tenantId: TENANT_ID };
    const exists = state.targets.some((t) => t.month === month);
    state.targets = exists
      ? state.targets.map((t) => (t.month === month ? saved : t))
      : [...state.targets, saved];
    return saved;
  },

  /* --- Inventario --- */

  async listInventoryCounts(): Promise<InventoryCount[]> {
    return [...state.inventoryCounts].sort((a, b) => a.month.localeCompare(b.month));
  },

  async getInventoryCount(month: string, locationId?: string): Promise<InventoryCount> {
    return (
      state.inventoryCounts.find((c) => c.month === month && sameLocation(c.locationId, locationId)) ??
      { tenantId: TENANT_ID, month, lines: [], totalValue: 0, updatedAt: nowIso() }
    );
  },

  async saveInventoryCount(month: string, input: InventoryCountInput): Promise<InventoryCount> {
    const { lines, totalValue } = valueInventoryLines(input.lines ?? [], state.ingredients);
    // Como en la API real: el centro principal se guarda sin locationId.
    const saved: InventoryCount = {
      tenantId: TENANT_ID,
      month,
      ...(input.locationId && input.locationId !== 'principal'
        ? { locationId: input.locationId }
        : {}),
      lines,
      totalValue,
      updatedAt: nowIso(),
    };
    const exists = state.inventoryCounts.some(
      (c) => c.month === month && sameLocation(c.locationId, input.locationId),
    );
    state.inventoryCounts = exists
      ? state.inventoryCounts.map((c) =>
          c.month === month && sameLocation(c.locationId, input.locationId) ? saved : c,
        )
      : [...state.inventoryCounts, saved];
    return saved;
  },

  async deleteInventoryCount(month: string, locationId?: string): Promise<void> {
    state.inventoryCounts = state.inventoryCounts.filter(
      (c) => !(c.month === month && sameLocation(c.locationId, locationId)),
    );
  },

  /* --- Analitica --- */

  async getPnl(period: Period) {
    return computeProfitAndLoss(financeInput(), period);
  },

  async getMonthlyPnl(period: Period) {
    return monthlyProfitAndLoss(financeInput(), period);
  },

  async getCashFlow(period: Period, opening = 0): Promise<CashFlowResponse> {
    const months = computeCashFlow(financeInput(), period, state.settings, opening);
    return {
      months,
      alerts: cashAlerts(months, state.tenant.currency),
      projection: projectCashFlow(months, 3),
    };
  },

  async getKpis(period: Period): Promise<KpisResponse> {
    const kpis = computeKpis(financeInput(), period);
    return { kpis, benchmarks: benchmarkKpis(kpis), overview: businessOverview(financeInput(), period) };
  },

  async getWeekday(period: Period) {
    return (demoLocation.operatingWeekdays?.length ? businessOverview(financeInput(), period).weekday : weekdayPerformance(state.revenues, period));
  },

  async getTargetComparison(month: string) {
    const target = state.targets.find((t) => t.month === month);
    return compareTargets(financeInput(), target, month);
  },

  async allocateTax(input: TaxAllocationRequest) {
    return allocateTaxPayment(financeInput(), input.payment, input.months, input.base);
  },

  async getDashboard(period: Period): Promise<DashboardResponse> {
    const input = financeInput();
    const kpis = computeKpis(input, period);
    const cashflow = computeCashFlow(input, period, state.settings, 0);
    // Un solo costeo para los dos usos: platos mas vendidos y avisos de carta.
    const carta = pricingItems();
    return {
      kpis,
      benchmarks: benchmarkKpis(kpis),
      target: state.targets.find((target) => target.month === period.end.slice(0, 7)) ?? null,
      pnl: computeProfitAndLoss(input, period),
      monthly: monthlyProfitAndLoss(input, period),
      cashflow,
      alerts: cashAlerts(cashflow, state.tenant.currency),
      payables: payablesResponse(todayIso()),
      weekday: (demoLocation.operatingWeekdays?.length ? businessOverview(financeInput(), period).weekday : weekdayPerformance(state.revenues, period)),
      topDishes: menuEngineering(
        carta.rows.map(({ item, analysis }) => ({ item, analysis })),
      ).slice(0, 5),
      carta: {
        items: carta.rows.map(({ analysis }) => ({
          menuItemId: analysis.menuItemId,
          name: analysis.name,
          platformName: analysis.platformName,
          alerts: analysis.alerts,
          costDeviationPct: analysis.costDeviationPct,
          grossMarginPct: analysis.grossMarginPct,
        })),
        skipped: carta.skipped,
      },
    };
  },

  /* --- Asistente --- */

  async getAssistantSuggestions(period: Period): Promise<AssistantSuggestion[]> {
    return demoSuggestions(assistantData(), period, todayIso());
  },

  /**
   * La propuesta de mapeo, en la demo.
   *
   * No hay modelo detras, asi que se usa el mismo adivinador por nombres de
   * columna que ya lleva el importador, y se dice. Sirve para recorrer el
   * flujo entero —propuesta, motivos, revision— que es de lo que va la demo;
   * con Bedrock delante, la propuesta la hace el modelo y acierta con
   * cabeceras que no se parecen a las nuestras, que es el caso dificil.
   */
  async proponerMapeoVentas(input: {
    columnas: string[];
    muestra: string[][];
    destinos: Array<{ key: string; label: string }>;
  }): Promise<MapeoVentasPropuesta> {
    const porUnidades = input.destinos.some((d) => d.key === 'dish');
    const adivinado = porUnidades
      ? guessUnitsColumns(input.columnas)
      : guessRevenueColumns(input.columnas, state.settings.platforms ?? []);
    const validos = new Set(input.destinos.map((d) => d.key));

    return {
      columnas: input.columnas.map((cabecera, indice) => {
        const key = adivinado[indice] ? targetKey(adivinado[indice]) : 'ignore';
        const reconocida = key !== 'ignore' && validos.has(key);
        return {
          indice,
          destino: reconocida ? key : 'ignore',
          confianza: reconocida ? ('alta' as const) : ('baja' as const),
          motivo: reconocida
            ? `El nombre «${cabecera}» coincide con este campo.`
            : `No reconocemos «${cabecera}»: dinos tú qué es.`,
        };
      }),
      ivaPista: { valor: 'no_se', motivo: 'En la demostración no se analiza el archivo.' },
      avisos: [
        'Estás en la demostración: la propuesta se hace por el nombre de las columnas, sin analizar el archivo.',
      ],
    };
  },

  async chatWithAssistant(input: AssistantChatRequest): Promise<AssistantChatResponse> {
    const last = [...input.messages].reverse().find((m) => m.role === 'user');
    if (!last || last.content.trim() === '') {
      throw new ApiError('VALIDATION_ERROR', 'Escribe una pregunta para Alexia.', 400);
    }
    await pause(500);
    // Desde el panel de ayuda: si la pregunta se parece a una FAQ de la
    // pantalla, se contesta con ella. En la demo no hay modelo detras y una
    // pregunta de «como hago X» no puede acabar en una cifra cualquiera.
    const faq = input.screen ? faqParecida(input.screen, last.content) : null;
    if (faq) return { reply: faq, toolCalls: [] };
    return demoChat(assistantData(), last.content, input.period, todayIso());
  },

  /* --- Facturacion --- */

  async changePlan(plan: 'basic' | 'pro', interval?: BillingInterval) {
    // En la demo el cambio es inmediato: es el escaparate del flujo. El
    // periodo se conserva si no se indica, como en la API.
    state.tenant = {
      ...state.tenant,
      plan,
      subscriptionStatus: 'active',
      billingInterval: interval ?? state.tenant.billingInterval ?? 'month',
    };
    return { ok: true as const };
  },

  async contractAddon(addon: 'escaner') {
    if (state.tenant.plan === 'pro') {
      throw new ApiError(
        'VALIDATION_ERROR',
        'El plan Pro ya incluye el escáner de facturas: no hay nada que contratar.',
        400,
      );
    }
    const addons = state.tenant.addons ?? [];
    state.tenant = {
      ...state.tenant,
      addons: addons.includes(addon) ? addons : [...addons, addon],
    };
    return { ok: true as const };
  },

  async cancelAddon(addon: 'escaner') {
    state.tenant = {
      ...state.tenant,
      addons: (state.tenant.addons ?? []).filter((a) => a !== addon),
    };
    return { ok: true as const };
  },

  /* En la demo hay dos facturas de ejemplo para que se vea la pantalla; el PDF
     no existe y la interfaz lo dice en vez de descargar un fichero vacio. */
  async getInvoices() {
    const hoy = todayIso();
    const mesPasado = addDaysIso(hoy, -30);
    return [
      { id: 'demo-2', numero: 'AR26/0002', fecha: hoy, baseCents: 4900, totalCents: 5929 },
      { id: 'demo-1', numero: 'AR26/0001', fecha: mesPasado, baseCents: 4900, totalCents: 5929 },
    ];
  },

  async getInvoicePdfUrl(): Promise<string> {
    throw new Error('En modo demostración las facturas son de ejemplo y no tienen PDF.');
  },

  async startCheckout(): Promise<{ url: string }> {
    throw new ApiError(
      'FORBIDDEN',
      'La suscripción no está disponible en el modo de demostración.',
      403,
    );
  },

  async startFounderCheckout(_code: string): Promise<{ url: string }> {
    throw new ApiError(
      'FORBIDDEN',
      'La suscripción Founder no está disponible en el modo de demostración.',
      403,
    );
  },

  async saveCancellationFeedback(reason) {
    state.tenant.cancellationFeedback = { reason, createdAt: new Date().toISOString() };
    return { ok: true };
  },
  async openBillingPortal(): Promise<{ url: string }> {
    throw new ApiError(
      'FORBIDDEN',
      'El portal de facturación no está disponible en el modo de demostración.',
      403,
    );
  },

  async setExtraSeats(): Promise<{ ok: true }> {
    throw new ApiError(
      'FORBIDDEN',
      'Los usuarios adicionales no están disponibles en el modo de demostración.',
      403,
    );
  },

  async setExtraLocations(): Promise<{ ok: true }> {
    throw new ApiError(
      'FORBIDDEN',
      'Los centros extra no están disponibles en el modo de demostración.',
      403,
    );
  },

  /* --- Equipo --- */

  async getTeam(): Promise<TeamResponse> {
    return {
      memberships: [state.user],
      seats: { used: 1, limit: 5, extraSeats: 0, extraSeatPrice: 5 },
    };
  },

  async inviteMember(): Promise<never> {
    throw new ApiError(
      'FORBIDDEN',
      'En la demo no se puede invitar a nadie. Crea tu cuenta para montar tu equipo.',
      403,
    );
  },

  async changeMemberRole(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'En la demo no se puede gestionar el equipo.', 403);
  },

  /*
   * Los roles personalizados SI funcionan en la demo: es una pantalla que se
   * entiende tocandola, y montar uno no toca a nadie porque en la demo el
   * equipo es una sola persona.
   */
  async listCustomRoles(): Promise<{ roles: CustomRole[] }> {
    return {
      roles: clone([
        ...PREDEFINED_ROLES.map((role) => ({ ...role, tenantId: TENANT_ID })),
        ...state.customRoles,
      ]),
    };
  },

  async createCustomRole(input: CustomRoleInput): Promise<{ role: CustomRole }> {
    if (state.customRoles.some((r) => r.name.toLowerCase() === input.name.toLowerCase())) {
      throw new ApiError('VALIDATION_ERROR', 'Ya tienes un rol con ese nombre.', 400);
    }
    const role: CustomRole = {
      tenantId: TENANT_ID,
      roleId: `rol-${state.customRoles.length + 1}-${Date.now()}`,
      name: input.name,
      permissions: input.permissions,
      createdAt: new Date().toISOString(),
    };
    state.customRoles = [...state.customRoles, role];
    return { role: clone(role) };
  },

  async updateCustomRole(roleId: string, input: CustomRoleInput): Promise<{ role: CustomRole }> {
    if (roleId.startsWith('preset:')) {
      throw new ApiError('VALIDATION_ERROR', 'Los roles predefinidos de Alexia no se pueden modificar.', 400);
    }
    const actual = state.customRoles.find((r) => r.roleId === roleId);
    if (!actual) throw new ApiError('NOT_FOUND', 'Ese rol no existe.', 404);
    const role: CustomRole = {
      ...actual,
      name: input.name,
      permissions: input.permissions,
      updatedAt: new Date().toISOString(),
    };
    state.customRoles = state.customRoles.map((r) => (r.roleId === roleId ? role : r));
    return { role: clone(role) };
  },

  async deleteCustomRole(roleId: string): Promise<{ ok: true }> {
    if (roleId.startsWith('preset:')) {
      throw new ApiError('VALIDATION_ERROR', 'Los roles predefinidos de Alexia no se pueden borrar.', 400);
    }
    state.customRoles = state.customRoles.filter((r) => r.roleId !== roleId);
    return { ok: true };
  },

  async deactivateMember(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'En la demo no se puede gestionar el equipo.', 403);
  },

  async activateMember(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'En la demo no se puede gestionar el equipo.', 403);
  },

  async resendInvitation(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'En la demo no se puede gestionar el equipo.', 403);
  },

  /* --- Importacion masiva del catalogo --- */

  async importIngredients(batch: IngredientBatch): Promise<BatchResult> {
    const result: BatchResult = { creados: 0, actualizados: 0, errores: [] };
    for (const nombre of batch.suppliers ?? []) {
      const existe = state.suppliers.some(
        (s) => s.name.trim().toLowerCase() === nombre.trim().toLowerCase(),
      );
      if (!existe) {
        state.suppliers = [
          ...state.suppliers,
          { tenantId: TENANT_ID, supplierId: newId('sup'), name: nombre.trim() },
        ];
      }
    }
    for (const [index, item] of batch.items.entries()) {
      const proveedor = item.supplierName
        ? state.suppliers.find(
            (s) => s.name.trim().toLowerCase() === item.supplierName!.trim().toLowerCase(),
          )
        : undefined;
      const base = { ...item.input, ...(proveedor ? { supplierId: proveedor.supplierId } : {}) };
      if (!item.id && base.createdVia === 'factura' && state.ingredients.some(ing => invoiceIngredientKey(ing) === invoiceIngredientKey(base))) {
        result.errores.push({ index, name: base.name, message: 'Este ingrediente y formato ya existen. Selecciónalo para actualizar su precio.' });
        continue;
      }
      if (item.id) {
        state.ingredients = state.ingredients.map((ing) =>
          ing.ingredientId === item.id
            ? { ...ing, ...base, pricePending: base.netPrice == null, priceEstimated: ing.priceEstimated, updatedAt: new Date().toISOString() }
            : ing,
        );
        result.actualizados += 1;
      } else {
        state.ingredients = [
          ...state.ingredients,
          {
            ...base,
            tenantId: TENANT_ID,
            pricePending: base.netPrice == null,
            priceEstimated: base.netPrice == null && base.purchaseUnit ? demoPriceEstimate(base.name, base.purchaseUnit, state.tenant.country, state.tenant.currency) ?? undefined : undefined,
            ingredientId: newId('ing'),
            updatedAt: new Date().toISOString(),
          } as Ingredient,
        ];
        result.creados += 1;
      }
    }
    await pause(300);
    return result;
  },

  async importRecipes(batch: RecipeBatch): Promise<BatchResult> {
    const result: BatchResult = { creados: 0, actualizados: 0, errores: [] };
    // Dos pasadas, como la API: una receta puede apuntar a otra del mismo
    // fichero, que hasta que no se crea no tiene id.
    const clave = (nombre: string) => nombre.trim().toLowerCase();
    const idPorNombre = new Map(state.recipes.map((r) => [clave(r.name), r.recipeId]));
    const pendientes: Array<{ recipeId: string; input: RecipeImportInput }> = [];

    for (const [index, original] of batch.items.entries()) {
      const item = { ...original, input: { ...original.input, components: original.input.components.map((comp) => {
        if (comp.ref.kind !== 'ingredient_del_fichero') return comp;
        const name = comp.ref.ingredientName;
        const matches = state.ingredients.filter((ing) => clave(ing.name) === clave(name));
        return matches.length === 1 ? { ...comp, ref: { kind: 'ingredient' as const, ingredientId: matches[0].ingredientId } } : comp;
      }) } };
      if (item.input.components.some((comp) => comp.ref.kind === 'ingredient_del_fichero')) {
        result.errores.push({ index, name: item.input.name, message: 'Confirma primero los ingredientes nuevos.' });
        continue;
      }
      const resueltas = item.input.components.filter(
        (comp) => comp.ref.kind !== 'recipe_del_fichero',
      );
      const primera = { ...item.input, components: resueltas };
      const recipeId = item.id ?? newId('rec');

      if (item.id) {
        state.recipes = state.recipes.map((r) =>
          r.recipeId === item.id
            ? ({ ...r, ...primera, updatedAt: new Date().toISOString() } as Recipe)
            : r,
        );
        result.actualizados += 1;
      } else {
        state.recipes = [
          ...state.recipes,
          {
            ...primera,
            tenantId: TENANT_ID,
            recipeId,
            updatedAt: new Date().toISOString(),
          } as unknown as Recipe,
        ];
        result.creados += 1;
      }
      idPorNombre.set(clave(item.input.name), recipeId);
      if (resueltas.length !== item.input.components.length) {
        pendientes.push({ recipeId, input: item.input });
      }
    }

    for (const { recipeId, input } of pendientes) {
      const components = input.components.map((comp) =>
        comp.ref.kind === 'recipe_del_fichero'
          ? { ...comp, ref: { kind: 'recipe' as const, recipeId: idPorNombre.get(clave(comp.ref.recipeName)) ?? '' } }
          : comp,
      );
      state.recipes = state.recipes.map((r) =>
        r.recipeId === recipeId
          ? ({ ...r, ...input, components, updatedAt: new Date().toISOString() } as unknown as Recipe)
          : r,
      );
    }

    await pause(300);
    return result;
  },

  /* --- Panel de plataforma --- */

  /* --- Escaner de facturas --- */

  async getScanQuota() {
    // En la demo el escaner se ensena funcionando: es de las cosas que mas
    // venden y esconderla detras de un "no disponible" seria tirar piedras
    // contra el escaparate. Lo que NO hay es OCR de verdad.
    const q = scanQuota(state.tenant.plan, state.tenant.addons ?? [], demoScans, currentMonthDemo());
    return { ...q, planName: PLANS[state.tenant.plan]?.name ?? state.tenant.plan };
  },

  async createScanUploadUrl(): Promise<never> {
    throw new ApiError(
      'INTERNAL',
      'En la demostración el escáner funciona con una factura de ejemplo: pulsa «Probar con una factura de ejemplo».',
      501,
    );
  },

  async scanReceipt(key: string, documentType?: PurchaseDocumentType) {
    // 1 escaneo = 1 página, como en la API.
    const paginas: ScannedDocument[] = key === 'demo-2-paginas' ? facturaDemoDosPaginas() : [FACTURA_DEMO];
    demoScans += paginas.length;
    const documento = fusionarPaginas(paginas);
    const draft = buildExpenseDraft(documento, {
      suppliers: state.suppliers.map((s) => ({ supplierId: s.supplierId, name: s.name })),
      country: state.tenant.country,
      vatPctPorDefecto: state.settings.vatPct,
      recientes: state.expenses,
      documentType,
      today: todayIso(),
    });
    const id = newId('draft');
    const now = new Date();
    drafts.push({ id, tenantId: TENANT_ID, kind: 'expense', source: 'demo', status: 'pending', payload: { proposal: draft, documentType }, summary: draft.concept, createdBy: { userSub: state.user.userId }, createdAt: now.toISOString(), expiresAt: Math.floor(now.getTime() / 1000) + DRAFT_TTL_SECONDS });
    const catalogDraftId = newId('draft');
    drafts.push({ id: catalogDraftId, tenantId: TENANT_ID, kind: 'catalog-prices', source: 'demo', status: 'pending', payload: invoiceLinesToCatalogProposal(documento.lines ?? [], state.ingredients), summary: 'Precios de la factura por revisar', createdBy: { userSub: state.user.userId }, createdAt: now.toISOString(), expiresAt: Math.floor(now.getTime() / 1000) + DRAFT_TTL_SECONDS });
    return {
      draftId: id, catalogDraftId,
      key: paginas.length > 1 ? 'demo/factura-2-paginas.pdf' : 'demo/factura-ejemplo.jpg',
      draft,
      catalogPrices: invoiceLinesToCatalogProposal(documento.lines ?? [], state.ingredients),
      quota: scanQuota(state.tenant.plan, state.tenant.addons ?? [], demoScans, currentMonthDemo()),
    };
  },

  /**
   * Hoja de recuento de ejemplo: las filas que devolveria el OCR de una hoja
   * impresa desde esta misma demo y rellenada a mano. Recorre el MISMO
   * emparejador que la app real: si se rompiera, se romperia aqui.
   */
  async scanInventorySheet(): Promise<InventoryScanResult> {
    demoScans += 1;
    const ings = [...state.ingredients].filter((i) => i.active).slice(0, 9);
    const cantidades = ['2,5', '4', '1/2', '12', '', '3', '0,75', '6', '1'];
    const hoja = {
      rows: [
        { cells: ['Ingrediente', 'Formato de compra', 'Cantidad contada'], confidence: 99 },
        ...ings.map((ing, i) => ({
          cells: [ingredientDisplayName(ing), purchaseFormatLabel(ing), cantidades[i] ?? ''],
          confidence: i === 2 ? 71 : 96,
        })),
        { cells: ['Sal gorda (no está en Alexia)', 'Saco de 5 kg', '2'], confidence: 90 },
      ],
    };
    return {
      key: 'demo/hoja-recuento-ejemplo.jpg',
      draft: buildInventoryDraft(hoja, state.ingredients),
      quota: scanQuota(state.tenant.plan, state.tenant.addons ?? [], demoScans, currentMonthDemo()),
    };
  },

  async getReceiptUrl(): Promise<never> {
    throw new ApiError(
      'INTERNAL',
      'En la demostración no se guardan documentos: los gastos de ejemplo no tienen factura adjunta.',
      501,
    );
  },

  async adminChangePlan(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'No disponible en la demostración.', 403);
  },

  async adminRetryHolded(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },

  async adminEnviarResumenSemanal(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },

  async adminMarkAssistedOnboarding(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async adminSetCortesia(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async adminSetInternal(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async adminSetBonusLocations(_tenantId: string, bonusLocations: number) {
    return { ok: true as const, bonusLocations };
  },
  async adminApplyCoupon(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'No disponible en la demostración.', 403);
  },

  async getAdminOverview(): Promise<AdminOverview> {
    // Existe solo para cumplir el contrato: en la demo nunca se llega aqui
    // (getMe no marca platformAdmin y la ruta /admin esta protegida).
    throw new ApiError(
      'FORBIDDEN',
      'El panel de administración no está disponible en el modo de demostración.',
      403,
    );
  },

  async getPlatformPnl(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'No disponible en la demostración.', 403);
  },

  async createPlatformRecurring(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async deletePlatformRecurring(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async setPlatformSettings(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async setPlatformSettlement(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'El panel de administración no está en la demo.', 403);
  },
  async createPlatformExpense(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'No disponible en la demostración.', 403);
  },

  async deletePlatformExpense(): Promise<never> {
    throw new ApiError('FORBIDDEN', 'No disponible en la demostración.', 403);
  },
};
